import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'sonner';

import {
  RubricGrader,
  rubricInvalidIds,
  rubricMarksToNumbers,
  rubricTotal,
  type RubricMarks,
} from './RubricGrader';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { trainerApi, type AssessmentRubric } from '@/lib/api/trainer';

/**
 * Records a mark against a student who handed nothing in.
 *
 * This is the only way a viva ever gets a grade: there is no submission to open
 * from the grading inbox, because nothing was submitted. The server creates the
 * row on demand (GradeStudentDirect) with submission_url NULL and is_late NULL,
 * so the mark counts towards readiness without ever being read as a missed
 * deadline.
 *
 * Deliberately the same RubricGrader the inbox uses. A viva marked against a
 * different-looking form than an assignment would drift from it, and the
 * student sees one published rubric either way.
 */
export function VivaGradeDialog({
  assessmentId,
  student,
  maxScore,
  rubric,
  existingScore,
  onClose,
  onGraded,
}: {
  assessmentId: string;
  /** null closes the dialog. */
  student: { id: string; name: string } | null;
  maxScore?: number;
  rubric?: AssessmentRubric;
  existingScore?: number;
  onClose: () => void;
  onGraded: () => void;
}) {
  const [score, setScore] = useState('');
  const [marks, setMarks] = useState<RubricMarks>({});
  const [feedback, setFeedback] = useState('');

  // A rubric with no criteria is not a rubric; fall back to the single box
  // rather than rendering an empty table with a Total of 0.
  const scheme = rubric && rubric.criteria.length > 0 ? rubric : null;
  const max = maxScore ?? 0;

  const save = useMutation({
    mutationFn: () =>
      trainerApi.gradeStudentDirect(assessmentId, student!.id, {
        // With a rubric the server derives the score from the marks, so this
        // is sent as 0 and ignored rather than computed twice.
        score: scheme ? 0 : Number(score),
        feedback,
        ...(scheme ? { rubric_scores: rubricMarksToNumbers(marks) } : {}),
      }),
    onSuccess: () => {
      toast.success(`Mark recorded for ${student?.name ?? 'student'}`);
      reset();
      onGraded();
      onClose();
    },
    onError: () => toast.error('Could not record that mark. Try again.'),
  });

  function reset() {
    setScore('');
    setMarks({});
    setFeedback('');
  }

  const scoreNum = Number(score);
  const valid = scheme
    ? rubricInvalidIds(scheme, marks).length === 0 &&
      Object.keys(rubricMarksToNumbers(marks)).length > 0 &&
      rubricTotal(marks) <= max
    : score !== '' && !isNaN(scoreNum) && scoreNum >= 0 && scoreNum <= max;

  return (
    <Dialog
      open={student != null}
      onOpenChange={(open) => {
        if (!open) {
          reset();
          onClose();
        }
      }}
    >
      {/* Scrolls rather than overflowing: a six-criterion rubric is taller than
          a 390px viewport, and a Save button below the fold is a Save button
          nobody presses. */}
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Record viva mark — {student?.name}</DialogTitle>
          <DialogDescription>
            {existingScore != null
              ? `Currently marked ${existingScore}${max ? `/${max}` : ''}. Saving replaces that mark.`
              : 'The student hands nothing in for a viva, so this mark is the record of it.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {scheme ? (
            <RubricGrader
              rubric={scheme}
              maxScore={max}
              marks={marks}
              onChange={setMarks}
              disabled={save.isPending}
            />
          ) : (
            <div>
              <label htmlFor="viva-score" className="mb-1 block text-xs font-medium text-muted-foreground">
                Score <span className="text-foreground">/ {max}</span>
              </label>
              <input
                id="viva-score"
                type="number"
                inputMode="decimal"
                min={0}
                max={max}
                step="any"
                value={score}
                onChange={(e) => setScore(e.target.value)}
                disabled={save.isPending}
                placeholder="0"
                className="w-24 rounded-lg border border-input bg-background px-3 py-1.5 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
          )}

          <div>
            <label htmlFor="viva-feedback" className="mb-1 block text-xs font-medium text-muted-foreground">
              Feedback <span className="opacity-70">(optional)</span>
            </label>
            <textarea
              id="viva-feedback"
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              rows={3}
              disabled={save.isPending}
              placeholder="What they explained well, and what to work on…"
              className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
          <Button onClick={() => save.mutate()} disabled={!valid || save.isPending}>
            {save.isPending ? 'Saving…' : 'Record mark'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
